import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { all_routes } from "../../Router/all_routes";
import { useSelector } from "react-redux";
import apiClient from "../../api/apiClient";
import { toast } from "sonner";
import { currencySettingsData } from "../../core/json/currencySettings";
import { API_BASE_URL } from "../../api/header";
import timezones from "timezones.json";
import { languageSettingsData } from "../../core/json/languageSettings";
import DefaultEditor from "react-simple-wysiwyg";
import {
  Info,
  MapPin,
  Globe,
  Percent,
  DollarSign,
  Shield,
  Monitor,
  Printer,
  X,
  Save,
  Image as ImageIcon,
  Loader2,
  ChevronLeft,
} from "lucide-react";
import MediaLibraryComponent from "../media/MediaLibraryComponent";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ReactSelect } from "@/components/ui/react-select";
import { Country, State } from "country-state-city";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { PageContainer } from "@/components/layout/PageContainer";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import RecordNotFound from "@/core/components/RecordNotFound";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const taxClassOptions = [
  { value: "GST", label: "GST" },
  { value: "VAT", label: "VAT" },
  { value: "NA", label: "None" },
  { value: "Other", label: "Other" },
];

const EditStore = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [store, setStore] = useState({
    name: "",
    alternate_name: "",
    logo: "",
    description: "",
    email: "",
    phone: "",
    mobile: "",
    fax: "",
    website: "",
    address: "",
    city: "",
    postcode: "",
    state: "",
    country: "",
    zip: "",
    time_zone: "",
    time_format: "",
    language: "",
    default_currency: "",
    price_format: "",
    decimal_format: "",
    default_tax_class: "NA",
    tax_percentage: 0,
    registration_no: "",
    start_time: "",
    end_time: "",
    default_address: "",
    api_key: "",
    accounting_method: "",
    send_email_from: "",
    is_send_email_from_verified: false,
    receive_all_emails: false,
    require_two_factor_auth: false,
    charge_restocking_fee: false,
    restocking_fee_percentage: 0,
    charge_deposit: false,
    deposit_amount: 0,
    charge_tax_on_deposit: false,
    deposit_tax_class: "",
    default_charge_deposit: false,
    lock_screen_enabled: false,
    screen_timeout_minutes: 1,
    terms_and_conditions: "",
    status: "active",
    print_agent_enabled: false,
    print_agent_pairing_key: "",
  });

  const currencyData = useSelector((state) => state.store_currency);

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageDetails, setSelectedImageDetails] = useState(null);

  const [showLogoutPopup, setShowLogoutPopup] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = all_routes.signin;
  };

  // Queries
  const { data: storeData } = useQuery({
    queryKey: ["store", id],
    queryFn: async () => {
      const response = await apiClient.get(`${API_BASE_URL}/store/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (storeData) {
      const sanitizedData = { ...storeData };
      Object.keys(sanitizedData).forEach((key) => {
        if (sanitizedData[key] === null) {
          sanitizedData[key] = "";
        }
      });
      // Normalize language: API may return an object {code, language} or a string code
      if (
        sanitizedData.language &&
        typeof sanitizedData.language === "object"
      ) {
        sanitizedData.language = sanitizedData.language.code || "";
      }
      // Normalize start_time / end_time: extract HH:MM from any format
      // API may return "HH:MM:SS", a full datetime "2026-05-13T09:00:00Z", etc.
      ["start_time", "end_time"].forEach((key) => {
        if (sanitizedData[key] && typeof sanitizedData[key] === "string") {
          const match = sanitizedData[key].match(
            /(\d{1,2}):(\d{2})(?::\d{2})?/,
          );
          if (match) {
            // Pad to HH:MM so <input type="time"> displays correctly
            sanitizedData[key] = match[1].padStart(2, "0") + ":" + match[2];
          } else {
            sanitizedData[key] = "";
          }
        }
      });
      // Normalize default_currency: API may return an object {id,...} or a scalar id
      // Keep the full object if available so we send the whole currency JSON to the backend.
      if (
        sanitizedData.default_currency &&
        typeof sanitizedData.default_currency === "object"
      ) {
        // already a full object - keep it as-is
      } else if (sanitizedData.default_currency) {
        // scalar id - look up the full currency object from local data
        const found = currencySettingsData.find(
          (c) => String(c.id) === String(sanitizedData.default_currency),
        );
        sanitizedData.default_currency =
          found || sanitizedData.default_currency;
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStore((prev) => ({ ...prev, ...sanitizedData }));
    }
  }, [storeData]);

  // Mutations
  const updateStoreMutation = useMutation({
    mutationFn: async (updatedStore) => {
      const response = await apiClient.put(
        `${API_BASE_URL}/store/${id}`,
        updatedStore,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      queryClient.invalidateQueries({ queryKey: ["store", id] });
      toast.success("Store updated successfully!");
      setShowLogoutPopup(true);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to update store");
    },
  });

  const fetchMediaDetails = async (mediaId) => {
    try {
      const res = await apiClient.get(`${API_BASE_URL}/media/${mediaId}`);
      if (res.status === 200) {
        setSelectedImageDetails(res.data.data);
        return res.data.data;
      }
    } catch (error) {
      console.log("Failed to fetch media details", error);
      return null;
    }
  };

  const handleMediaSelect = async (selectedId) => {
    setSelectedImage(selectedId);
    const data = await fetchMediaDetails(selectedId);
    if (data) {
      setStore({
        ...store,
        logo: data.url || "",
      });
    }
  };

  useEffect(() => {
    if (store.id && currencyData) {
      if (currencyData.store_id === store.id) {
        const found = currencySettingsData.find(
          (c) => String(c.id) === String(currencyData.currency?.id || ""),
        );
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setStore((prev) => ({
          ...prev,
          default_currency:
            found || currencyData.currency || prev.default_currency,
        }));
      }
    }
  }, [store.id, currencyData]);

  const validateFields = () => {
    if (store.name === "") {
      toast.error("Store name is required!");
      return false;
    }
    if (
      !store.default_currency ||
      store.default_currency === "" ||
      (typeof store.default_currency === "object" &&
        !store.default_currency?.id)
    ) {
      toast.error("Default currency is required!");
      return false;
    }
    return true;
  };

  const countries = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        value: c.isoCode,
        label: c.name,
      })),
    [],
  );

  const selectedCountryCode = useMemo(() => {
    const c = Country.getAllCountries().find((c) => c.name === store.country);
    return c ? c.isoCode : "";
  }, [store.country]);

  const stateOptions = useMemo(
    () =>
      selectedCountryCode
        ? State.getStatesOfCountry(selectedCountryCode).map((s) => ({
          value: s.name,
          label: s.name,
        }))
        : [],
    [selectedCountryCode],
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === "checkbox" ? checked : value;

    // Handle numeric fields
    if (
      [
        "tax_percentage",
        "restocking_fee_percentage",
        "deposit_amount",
        "screen_timeout_minutes",
      ].includes(name)
    ) {
      finalValue = value === "" ? "" : parseFloat(value);
    }

    setStore({
      ...store,
      [name]: finalValue,
    });
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      return;
    }
    const payload = {
      ...store,
      start_time: store.start_time || null,
      end_time: store.end_time || null,
    };
    updateStoreMutation.mutate(payload);
  };

  const handleNavigation = (route) => {
    navigate(route);
  };

  const isLoading = updateStoreMutation.isPending;

  if (!hasPermission("store update")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Edit Store"
          subtitle={`Update ${store.name || "store"} profile and configuration`}
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            onClick={() => handleNavigation(all_routes.storeList)}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="space-y-6 pb-12"
      >
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Store Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={store.name || ""}
                  onChange={handleChange}
                  placeholder="Enter store name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="alternate_name">Alternate Name</Label>
                <Input
                  id="alternate_name"
                  name="alternate_name"
                  value={store.alternate_name || ""}
                  onChange={handleChange}
                  placeholder="Enter alternate name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="registration_no">Registration Number</Label>
                <Input
                  id="registration_no"
                  name="registration_no"
                  value={store.registration_no || ""}
                  onChange={handleChange}
                  placeholder="Business registration number"
                />
              </div>

              <div className="md:col-span-3 space-y-2">
                <Label>Logo</Label>
                <div className="flex items-center gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsMediaLibraryOpen(true)}
                  >
                    <ImageIcon className="mr-2 h-4 w-4" />
                    {store.logo ? "Change Logo" : "Select Logo"}
                  </Button>
                  {(selectedImageDetails || store.logo) && (
                    <div className="flex items-center gap-2">
                      <div className="relative h-20 w-20 border rounded-lg overflow-hidden bg-muted">
                        <img
                          src={selectedImageDetails?.url || store.logo}
                          alt="Store Logo"
                          className="h-full w-full object-contain"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          setSelectedImageDetails(null);
                          setStore((prev) => ({ ...prev, logo: "" }));
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Store Email</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  value={store.email || ""}
                  onChange={handleChange}
                  placeholder="store@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={store.phone || ""}
                  onChange={handleChange}
                  placeholder="Phone number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile</Label>
                <Input
                  id="mobile"
                  name="mobile"
                  value={store.mobile || ""}
                  onChange={handleChange}
                  placeholder="Mobile number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <ReactSelect
                  options={[
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                  value={{
                    value: store.status,
                    label:
                      store.status.charAt(0).toUpperCase() +
                      store.status.slice(1),
                  }}
                  onChange={(opt) =>
                    setStore({ ...store, status: opt ? opt.value : "active" })
                  }
                />
              </div>

              <div className="md:col-span-1 space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  name="website"
                  value={store.website || ""}
                  onChange={handleChange}
                  placeholder="https://example.com"
                />
              </div>

              <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Description</Label>
                  <DefaultEditor
                    value={store.description || ""}
                    onChange={(e) =>
                      setStore({ ...store, description: e.target.value })
                    }
                    className="min-h-[150px] border rounded-md"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Terms and Conditions</Label>
                  <DefaultEditor
                    value={store.terms_and_conditions || ""}
                    onChange={(e) =>
                      setStore({
                        ...store,
                        terms_and_conditions: e.target.value,
                      })
                    }
                    className="min-h-[150px] border rounded-md"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Address Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-4 space-y-2">
                <Label htmlFor="address">Address</Label>
                <AddressAutocomplete
                  id="address"
                  name="address"
                  value={store.address || ""}
                  onChange={(val) =>
                    setStore((prev) => ({ ...prev, address: val }))
                  }
                  onPlaceSelect={({
                    address,
                    city,
                    state,
                    country,
                    postcode,
                  }) =>
                    setStore((prev) => ({
                      ...prev,
                      address: address || prev.address,
                      city: city || prev.city,
                      state: state || prev.state,
                      country: country || prev.country,
                      postcode: postcode || prev.postcode,
                    }))
                  }
                  placeholder="Search for address…"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={store.city || ""}
                  onChange={handleChange}
                  placeholder="City"
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <ReactSelect
                  options={countries}
                  value={
                    countries.find((c) => c.label === store.country) || null
                  }
                  onChange={(opt) =>
                    setStore({
                      ...store,
                      country: opt ? opt.label : "",
                      state: "",
                    })
                  }
                  placeholder="Select country"
                  isClearable
                />
              </div>
              <div className="space-y-2">
                <Label>State/Province</Label>
                <ReactSelect
                  options={stateOptions}
                  value={
                    stateOptions.find((s) => s.value === store.state) || null
                  }
                  onChange={(opt) =>
                    setStore({ ...store, state: opt ? opt.value : "" })
                  }
                  placeholder={
                    selectedCountryCode
                      ? "Select state"
                      : "Select country first"
                  }
                  isDisabled={!selectedCountryCode}
                  isClearable
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">Postal Code</Label>
                <Input
                  id="postcode"
                  name="postcode"
                  value={store.postcode || ""}
                  onChange={handleChange}
                  placeholder="Postal Code"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Regional Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              Regional Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Time Zone</Label>
                <ReactSelect
                  options={timezones.map((tz) => ({
                    value: `${tz.value}-${tz.abbr}`,
                    label: tz.text,
                  }))}
                  value={
                    store.time_zone
                      ? {
                        value: store.time_zone,
                        label: timezones.find(
                          (tz) =>
                            `${tz.value}-${tz.abbr}` === store.time_zone,
                        )?.text,
                      }
                      : null
                  }
                  onChange={(opt) =>
                    setStore({ ...store, time_zone: opt ? opt.value : "" })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Time Format</Label>
                <ReactSelect
                  options={[
                    { value: "12h", label: "12-hour (AM/PM)" },
                    { value: "24h", label: "24-hour" },
                  ]}
                  value={
                    store.time_format
                      ? {
                        value: store.time_format,
                        label:
                          store.time_format === "12h" ? "12-hour" : "24-hour",
                      }
                      : null
                  }
                  onChange={(opt) =>
                    setStore({ ...store, time_format: opt ? opt.value : "" })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Language</Label>
                <ReactSelect
                  options={languageSettingsData.map((lang) => ({
                    value: lang.code,
                    label: lang.language,
                  }))}
                  value={
                    languageSettingsData
                      .map((l) => ({ value: l.code, label: l.language }))
                      .find((opt) => opt.value === store.language) || null
                  }
                  onChange={(opt) =>
                    setStore({ ...store, language: opt?.value || "" })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Default Currency <span className="text-destructive">*</span>
                </Label>
                <ReactSelect
                  options={currencySettingsData.map((c) => ({
                    value: String(c.id),
                    label: `${c.name} (${c.symbol})`,
                    currency: c,
                  }))}
                  value={
                    currencySettingsData
                      .map((c) => ({
                        value: String(c.id),
                        label: `${c.name} (${c.symbol})`,
                        currency: c,
                      }))
                      .find(
                        (opt) =>
                          opt.value ===
                          String(
                            typeof store.default_currency === "object"
                              ? store.default_currency?.id
                              : store.default_currency,
                          ),
                      ) || null
                  }
                  onChange={(opt) =>
                    setStore({
                      ...store,
                      default_currency: opt?.currency || "",
                    })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Store Hours</Label>
                <div className="flex gap-2">
                  <Input
                    type="time"
                    name="start_time"
                    value={store.start_time || ""}
                    onChange={handleChange}
                  />
                  <Input
                    type="time"
                    name="end_time"
                    value={store.end_time || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tax & Sales Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-primary" />
              Tax & Sales Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Default Tax Class</Label>
                <ReactSelect
                  options={taxClassOptions}
                  value={
                    taxClassOptions.find(
                      (opt) => opt.value === store.default_tax_class,
                    ) ||
                    (store.default_tax_class
                      ? taxClassOptions.find((opt) => opt.value === "Other")
                      : null)
                  }
                  onChange={(opt) =>
                    setStore({
                      ...store,
                      default_tax_class: opt?.value || "",
                      tax_percentage:
                        opt?.value === "NA" ? 0 : store.tax_percentage,
                    })
                  }
                />
                {(!["GST", "VAT", "NA", ""].includes(store.default_tax_class) ||
                  store.default_tax_class === "Other") && (
                    <Input
                      className="mt-2"
                      placeholder="Enter custom tax class"
                      value={
                        store.default_tax_class === "Other"
                          ? ""
                          : store.default_tax_class
                      }
                      onChange={(e) =>
                        setStore({
                          ...store,
                          default_tax_class: e.target.value,
                        })
                      }
                    />
                  )}
              </div>
              <div className="space-y-2">
                <Label>Tax Percentage (%)</Label>
                <Input
                  type="number"
                  name="tax_percentage"
                  value={store.tax_percentage || ""}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  disabled={store.default_tax_class === "NA"}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Refund & Deposit Settings */}
        <Card style={{ display: 'none' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Refund & Deposit Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Charge Restocking Fee</Label>
                  <Switch
                    checked={store.charge_restocking_fee}
                    onCheckedChange={(checked) =>
                      setStore({ ...store, charge_restocking_fee: checked })
                    }
                  />
                </div>
                {store.charge_restocking_fee && (
                  <div className="space-y-2 animate-in slide-in-from-top-2">
                    <Label>Restocking Fee (%)</Label>
                    <Input
                      type="number"
                      name="restocking_fee_percentage"
                      value={store.restocking_fee_percentage || ""}
                      onChange={handleChange}
                      min="0"
                      max="100"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Charge Deposit</Label>
                  <Switch
                    checked={store.charge_deposit}
                    onCheckedChange={(checked) =>
                      setStore({ ...store, charge_deposit: checked })
                    }
                  />
                </div>
                {store.charge_deposit && (
                  <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-top-2">
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        name="deposit_amount"
                        value={store.deposit_amount || ""}
                        onChange={handleChange}
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Deposit Tax Class</Label>
                      <ReactSelect
                        options={taxClassOptions}
                        value={
                          taxClassOptions.find(
                            (opt) => opt.value === store.deposit_tax_class,
                          ) || null
                        }
                        onChange={(opt) =>
                          setStore({
                            ...store,
                            deposit_tax_class: opt?.value || "",
                          })
                        }
                        placeholder="Select tax class"
                      />
                    </div>
                    <div className="col-span-2 flex items-center justify-between">
                      <Label>Tax on Deposit</Label>
                      <Switch
                        checked={store.charge_tax_on_deposit}
                        onCheckedChange={(checked) =>
                          setStore({ ...store, charge_tax_on_deposit: checked })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security & Screen */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ display: 'none' }}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                <div className="space-y-0.5">
                  <Label>Two-Factor Auth</Label>
                  <p className="text-xs text-muted-foreground">
                    Mandatory 2FA for users
                  </p>
                </div>
                <Switch
                  checked={store.require_two_factor_auth}
                  onCheckedChange={(checked) =>
                    setStore({ ...store, require_two_factor_auth: checked })
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5 text-primary" />
                Screen Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Lock Screen</Label>
                  <p className="text-xs text-muted-foreground">
                    Auto-lock on inactivity
                  </p>
                </div>
                <Switch
                  checked={store.lock_screen_enabled}
                  onCheckedChange={(checked) =>
                    setStore({ ...store, lock_screen_enabled: checked })
                  }
                />
              </div>
              {store.lock_screen_enabled && (
                <div className="space-y-2 animate-in slide-in-from-top-2">
                  <Label>Timeout (Minutes)</Label>
                  <Input
                    type="number"
                    name="screen_timeout_minutes"
                    value={store.screen_timeout_minutes || ""}
                    onChange={handleChange}
                    min="1"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Print Agent Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5 text-primary" />
              Print Agent Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
              <div className="space-y-0.5">
                <Label>Enable Print Agent</Label>
                <p className="text-xs text-muted-foreground">
                  Allow this store to use the print agent service
                </p>
              </div>
              <Switch
                checked={!!store.print_agent_enabled}
                onCheckedChange={(checked) =>
                  setStore({ ...store, print_agent_enabled: checked })
                }
              />
            </div>
            {store.print_agent_enabled && (
              <div className="space-y-2 animate-in slide-in-from-top-2">
                <Label htmlFor="print_agent_pairing_key">Pairing Key</Label>
                <Input
                  id="print_agent_pairing_key"
                  name="print_agent_pairing_key"
                  value={store.print_agent_pairing_key || ""}
                  onChange={handleChange}
                  placeholder="Enter print agent pairing key"
                  maxLength={255}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleNavigation(all_routes.storeList)}
            className="w-32"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-40"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Update Store
              </>
            )}
          </Button>
        </div>
      </form>

      <MediaLibraryComponent
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onSelect={handleMediaSelect}
        isMultiple={false}
        selectedIds={selectedImage ? [selectedImage] : []}
      />

      <AlertDialog open={showLogoutPopup} onOpenChange={setShowLogoutPopup}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Store Settings Updated</AlertDialogTitle>
            <AlertDialogDescription>
              Store settings have been successfully updated. You must log out
              and log in again for the changes to take effect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleLogout}>
              Log Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default EditStore;
